import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createMeeting as createMeetingAPI } from '@/api/v4/meetings.api';
import { notify } from '@/ui/snackbar/notify';
import { QueryKey } from '@/config/query-client';
import { useCompanyFetchMode } from '@/features/companies/overview/company-fetch-context';

export const useCreateMeeting = () => {
  const { t } = useTranslation('default');
  const queryClient = useQueryClient();
  const fetchMode = useCompanyFetchMode();

  const mutation = useMutation({
    mutationFn: createMeetingAPI,
    onSuccess: async meeting => {
      await Promise.all([
        ...(meeting.organizationId
          ? [
              queryClient.invalidateQueries({
                queryKey: [QueryKey.Company, fetchMode, meeting.organizationId],
              }),
              queryClient.invalidateQueries({
                queryKey: [QueryKey.CompanyMeetings, meeting.organizationId],
              }),
              queryClient.invalidateQueries({
                queryKey: [QueryKey.CompanyActionsInfo],
              }),
            ]
          : []),
        queryClient.invalidateQueries({ queryKey: [QueryKey.LastActivities] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.LastActivitiesCount],
        }),
        ...(meeting.projectId
          ? [
              queryClient.invalidateQueries({
                queryKey: [QueryKey.Project, meeting.projectId],
              }),
              queryClient.invalidateQueries({
                queryKey: [QueryKey.ProjectMeetings, meeting.projectId],
              }),
            ]
          : []),
        ...(meeting.teamId
          ? [
              queryClient.invalidateQueries({
                queryKey: [QueryKey.Team, meeting.teamId],
              }),
              queryClient.invalidateQueries({
                queryKey: [QueryKey.TeamMeetings, meeting.teamId],
              }),
            ]
          : []),
      ]);
    },
    onError: () => {
      notify({
        message: t`unknownError`,
      });
    },
  });

  return {
    createMeeting: mutation.mutateAsync,
    isError: mutation.isError,
  };
};
